import type { PicksRole } from "./types";

export const PICKS_CAPABILITY_KEYS = [
  "viewCatalog",
  "viewPartners",
  "viewCategories",
  "createPick",
  "editContent",
  "editMedia",
  "transitions",
  "editLinks",
  "editTerms",
  "editCompliance",
  "runLinkCheck",
  "runTestGo",
  "editPartners",
  "editReferences",
  "viewB2B",
] as const;

export type PicksCapabilityKey = (typeof PICKS_CAPABILITY_KEYS)[number];

export type PicksCapabilities = Readonly<
  Record<PicksCapabilityKey, boolean> & { readOnly: boolean }
>;

export const PICKS_TABS = [
  "basics",
  "places",
  "options",
  "media",
  "links",
  "publishing",
  "partnerTerms",
] as const;

export type PicksTab = (typeof PICKS_TABS)[number];

const BASE_TABS: ReadonlyArray<PicksTab> = [
  "basics",
  "places",
  "options",
  "media",
  "links",
  "publishing",
];

function build(
  granted: ReadonlyArray<PicksCapabilityKey>,
  readOnly: boolean,
): PicksCapabilities {
  const caps: Record<PicksCapabilityKey, boolean> = {
    viewCatalog: false,
    viewPartners: false,
    viewCategories: false,
    createPick: false,
    editContent: false,
    editMedia: false,
    transitions: false,
    editLinks: false,
    editTerms: false,
    editCompliance: false,
    runLinkCheck: false,
    runTestGo: false,
    editPartners: false,
    editReferences: false,
    viewB2B: false,
  };
  for (const key of granted) {
    caps[key] = true;
  }
  return Object.freeze({ ...caps, readOnly });
}

const READ_ONLY_GRANTS: ReadonlyArray<PicksCapabilityKey> = [
  "viewCatalog",
  "viewPartners",
  "viewCategories",
];

const MODERATOR_GRANTS: ReadonlyArray<PicksCapabilityKey> = [
  "viewCatalog",
  "viewCategories",
  "createPick",
  "editContent",
  "editMedia",
  "transitions",
];

type KnownRole = "moderator" | "superadmin" | "support" | "analyst";

function knownRole(role: unknown): KnownRole | null {
  switch (role) {
    case "moderator":
    case "superadmin":
    case "support":
    case "analyst":
      return role;
    default:
      return null;
  }
}

export function picksCapabilities(
  role: PicksRole | null | undefined | unknown,
): PicksCapabilities {
  const known = knownRole(role);
  if (known === null) {
    return build([], false);
  }
  switch (known) {
    case "superadmin":
      return build(PICKS_CAPABILITY_KEYS, false);
    case "moderator":
      return build(MODERATOR_GRANTS, false);
    case "support":
    case "analyst":
      return build(READ_ONLY_GRANTS, true);
    default: {
      const unreachable: never = known;
      return unreachable;
    }
  }
}

export function tabsForRole(
  role: PicksRole | null | undefined | unknown,
): ReadonlyArray<PicksTab> {
  const known = knownRole(role);
  if (known === null) {
    return [];
  }
  return known === "superadmin"
    ? [...BASE_TABS, "partnerTerms"]
    : [...BASE_TABS];
}
